import React, { useState } from 'react';
import { Box, Button } from '@mui/material';

import { PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import { PlateManufacturer, PlateType } from '@paragon/contract-library';
import { NewPlatePrice } from '@paragon/data-connections';
import { hasPricesEditPermission } from '@paragon/login-tools';
import { NumericField } from '@paragon/material';

import { insertPlatePrice } from '../actions/platePriceActions';
import getDefaultPlateTypesByManufacturer from '../selectors/getDefaultPlateTypesByManufacturer';
import { CallToUpgrade } from './CallToUpgrade';
import { useAppDispatch, useAppSelector } from './hooks';
import { PriceActionBar } from './PriceActionBar';

const plateManufacturerOptions: readonly (PlateManufacturer | 'Any')[] = [
  'MiTek',
  'Alpine',
  'Eagle',
  'Simpson',
  'CH Machine',
  'Cherokee Metal',
  'Any',
];

export function InsertPlatePrice({
  onClose,
  onHeightChange,
}: {
  readonly onClose: () => void;
  readonly onHeightChange: (height: number) => void;
}) {
  const dispatch = useAppDispatch();
  const defaultPlateTypeProperties = useAppSelector(getDefaultPlateTypesByManufacturer);
  const hasEditPermission = useAppSelector(hasPricesEditPermission);

  const [plateManufacturer, setPlateManufacturer] = useState<PlateManufacturer | 'Any' | null>(null);
  const [plateType, setPlateType] = useState<PlateType | null>(null);
  const [pricePerPound, setPricePerPound] = useState<number>(0);
  const [notes, setNotes] = useState<string | null>(null);

  async function onInsertClick(): Promise<void> {
    if (plateType === null) throw new Error('Plate type not specified');
    const newPlatePrice: NewPlatePrice = { plateType, pricePerPound, notes };
    if (await dispatch(insertPlatePrice(newPlatePrice))) onClose();
  }

  const insertDisabled = plateManufacturer === null || plateType === null || pricePerPound === 0 || !hasEditPermission;

  return (
    <PriceActionBar
      onClose={onClose}
      onHeightChange={onHeightChange}
      sx={{
        gap: 2,

        '& > *': {
          flex: '1 0 200px',
          margin: '0!important',
        },
      }}
    >
      <PropertySelectField<PlateManufacturer | 'Any'>
        property="Plate Manufacturer"
        value={plateManufacturer}
        submit={(plateManufacturer) => void setPlateManufacturer(plateManufacturer)}
        options={plateManufacturerOptions}
        disabled={!hasEditPermission}
      />
      <PropertySelectField<PlateType>
        property="Plate Type"
        value={plateType}
        submit={(plateType) => void setPlateType(plateType)}
        options={plateManufacturer ? defaultPlateTypeProperties[plateManufacturer] : []}
        disabled={plateManufacturer === null || !hasEditPermission}
      />
      <NumericField
        value={pricePerPound}
        label="Price per Pound"
        min={0}
        disabled={!hasEditPermission}
        onSubmit={(pricePerPound) => void setPricePerPound(pricePerPound)}
      />
      <PropertyTextInput
        value={notes ?? ''}
        property="Notes"
        disabled={!hasEditPermission}
        submit={(notes) => void setNotes(notes === '' ? null : notes)}
      />
      <Box sx={{ alignItems: 'center', display: 'flex' }}>
        {hasEditPermission ? (
          <Button
            variant="contained"
            size="small"
            onClick={onInsertClick}
            disabled={insertDisabled}
            sx={{ flex: 1, minHeight: 40 }}
          >
            Insert Plate Price
          </Button>
        ) : (
          <CallToUpgrade />
        )}
      </Box>
    </PriceActionBar>
  );
}
